'use client';

import Icon from '@/components/ui/AppIcon';

interface CarbonCredits70CTAProps {
  currentLanguage?: string;
}

const CarbonCredits70CTA = ({ currentLanguage = 'en' }: CarbonCredits70CTAProps) => {
  return (
    <section className="py-10 bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 relative overflow-hidden">
      {/* Animated background shimmer */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-yellow-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Left: Badge + Text */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0 border border-white/30">
              <Icon name="SparklesIcon" size={32} className="text-yellow-300" />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-400/30 rounded-full text-yellow-100 text-xs font-bold uppercase tracking-wider mb-1">
                <Icon name="StarIcon" size={12} />
                CAQSG™ Quantum-AI MRV Technology
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-white leading-tight">
                Get <span className="text-yellow-300 text-3xl md:text-4xl">70% More</span> Carbon Credits for Your Projects
              </h2>
              <p className="text-white/85 text-sm mt-1 max-w-xl">
                GenCarbon's proprietary CAQSG™ quantum-classical hybrid MRV reduces uncertainty by 30–40% and delivers 2.5–3× higher carbon yields via sub-hectare hotspot zoning. First credits in 12–18 months.
              </p>
            </div>
          </div>

          {/* Right: CTA Button */}
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href="/enterprise-government-solutions"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-green-700 font-bold text-base rounded-xl hover:bg-yellow-50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <Icon name="ChartBarIcon" size={20} />
              Know How It Works
            </a>
            <a
              href="mailto:nupurbanker@gmail.com?subject=70% More Carbon Credits Inquiry&body=Hello Dr. Nupur, I am interested in learning how CAQSG™ can help my project get 70% more carbon credits."
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-yellow-400 text-green-900 font-bold text-base rounded-xl hover:bg-yellow-300 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <Icon name="EnvelopeIcon" size={20} />
              Get Started Free
            </a>
          </div>
        </div>

        {/* Stats Row */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: '70%', label: 'More Carbon Credits', icon: 'ArrowTrendingUpIcon' },
            { value: '30–40%', label: 'Lower MRV Uncertainty', icon: 'ShieldCheckIcon' },
            { value: '12–18 mo', label: 'First Credit Issuance', icon: 'ClockIcon' },
            { value: '2.5–3×', label: 'Higher Carbon Yields', icon: 'SparklesIcon' },
          ].map((stat, i) => (
            <div key={i} className="bg-white/15 backdrop-blur-sm rounded-xl p-3 text-center border border-white/20">
              <Icon name={stat.icon as any} size={20} className="text-yellow-300 mx-auto mb-1" />
              <p className="text-white font-bold text-xl">{stat.value}</p>
              <p className="text-white/75 text-xs">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CarbonCredits70CTA;
